import type { Event } from '@patsnap/tahiti-core';
import type { SubscriptionListDTO } from '@telecom/shared/domain/permission/apis/stripe-api';
import type { IPackageType } from '@telecom/shared/domain/permission/apis/package-api';

export enum UserLevel {
    Guest = 'guest',
    Free = 'free',
    Pro = 'pro',
    Enterprise = 'enterprise'
}

/**
 * 用户信息服务
 *
 * 提供用户相关的信息和状态，包括：
 * - 判断是否为个人租户
 * - 判断是否为企业租户
 * - 判断是否为 Pro 用户
 * - 判断是否为免费用户
 * - 用户订阅信息管理
 */
export abstract class UserInfoService {
    /**
     * 订阅信息变化事件
     */
    abstract readonly onSubscriptionChange: Event<void>;

    /**
     * 是否为企业租户
     * 判断逻辑：company_id 存在则为企业租户
     */
    abstract isEnterpriseTenant(): boolean;

    /**
     * 是否为个人租户
     * 判断逻辑：company_id 不存在则为个人租户
     */
    abstract isPersonalTenant(): boolean;

    /**
     * 是否为 Pro 用户
     * 判断逻辑：存在有效订阅则为 Pro 用户
     */
    abstract isPro(): boolean;

    /**
     * 是否为免费用户
     * 判断逻辑：是个人租户且不是 Pro 用户
     */
    abstract isFreeUser(): boolean;

    /**
     * 用户档位
     */
    abstract userLevel(): Promise<UserLevel>;

    /**
     * 获取当前订阅信息
     */
    abstract getCurrentSubscription(): SubscriptionListDTO | null;
    /**
     * 获取IP有效的订阅套餐
     * @returns 存在 eureka_package 为 ip_checking_pro 且 package_type 为 COMMERCIAL 的套餐时返回该套餐，否则返回 null
     */
    abstract getIpSearchingActivePackage(): IPackageType | null;

    /**
     * 是否正在加载订阅信息
     */
    abstract isLoadingSubscription(): boolean;

    /**
     * 获取用户订阅信息
     */
    abstract fetchSubscriptionList(): Promise<void>;
}
